import { HOURS } from "./window";

/**
 * The week a weekly job is about.
 *
 * A worker cannot pace a weekly job on a timer. It restarts, and a second
 * worker has its own timer, and both of those turn "weekly" into "whenever
 * this process last started". The cadence has to be a property of the calendar
 * rather than of the process, so the job asks which week it is in and the
 * event log's idempotency key does the rest (section 5).
 *
 * Monday, UTC. Both halves are choices and neither is derivable from the
 * vault, which says "weekly" and no more (FINDINGS TA-93). Monday because a
 * digest about last week should not land in the middle of it, and UTC because
 * the system has no shop timezone yet: inventing one here would put the
 * boundary in a different place than every other stamp in the database.
 */
export const WEEK_MS = 7 * 24 * HOURS;

/** Midnight UTC on the Monday of this instant's week. */
export function weekStart(at: Date): Date {
  const d = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  // getUTCDay is 0 for Sunday, which is the last day of the week here and not
  // the first. Sunday therefore steps back six days, not zero.
  const shift = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return d;
}

/**
 * The week as a key: its own Monday, as `YYYY-MM-DD`.
 *
 * A date rather than an ISO week number, because `2026-W37` needs a reader to
 * know the ISO week rules to say what it means, and this appears in log lines
 * and idempotency keys that people read at three in the morning. It also sorts
 * chronologically as a plain string, which the week number stops doing at a
 * year boundary.
 */
export function weekKey(at: Date): string {
  return weekStart(at).toISOString().slice(0, 10);
}
